import React, { useEffect, useState } from 'react';
import { ShoppingList, User } from '../types';
import { encodeSharedListData, buildWhatsAppUrl, copyTextToClipboard, ensureListShareToken } from '../services/storageService';

interface ShareListWhatsAppModalProps {
  isOpen: boolean;
  onClose: () => void;
  list: ShoppingList;
  currentUser: User | null;
  targetEmail?: string;
  isUnknownUser?: boolean;
}

export const ShareListWhatsAppModal: React.FC<ShareListWhatsAppModalProps> = ({
  isOpen,
  onClose,
  list,
  currentUser,
  targetEmail,
  isUnknownUser = false,
}) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [copied, setCopied] = useState(false);
  const [shareToken, setShareToken] = useState<string | undefined>(list.shareToken);

  // Deelsleutel zorgen: daarmee kan de ontvanger live meedoen (alleen als je ingelogd bent)
  useEffect(() => {
    if (!isOpen) return;
    if (list.shareToken) { setShareToken(list.shareToken); return; }
    setShareToken(ensureListShareToken(currentUser, list)?.shareToken);
  }, [isOpen, list.id, list.shareToken, currentUser?.id]);

  if (!isOpen) return null;

  const isLive = !!shareToken;
  const inviterName = currentUser?.name || list.ownerName || 'Een superMarct gebruiker';
  const encodedData = encodeSharedListData({ ...list, shareToken }, inviterName);
  
  // Construct absolute universal invite link
  const origin = window.location.origin;
  const shareUrl = `${origin}/?share_list_data=${encodedData}`;

  // Pre-filled WhatsApp message
  const whatsappMessage = `Hoi! 👋 ${inviterName} heeft de boodschappenlijst '${list.name}' (${list.items?.length || 0} artikelen) met je gedeeld in superMarct.\n\nKlik op deze link om direct te openen:\n${shareUrl}\n\n📱 Tip: Heb je superMarct al geïnstalleerd? Dan opent de app direct met de gedeelde lijst! 🛒✨`;

  // Format phone number to international WhatsApp format (e.g. 06 12345678 -> 31612345678)
  const formatPhoneForWhatsApp = (raw: string) => {
    let digits = raw.replace(/\D/g, '');
    if (digits.startsWith('06')) {
      digits = '316' + digits.substring(2);
    } else if (digits.startsWith('0031')) {
      digits = digits.substring(2);
    } else if (digits.startsWith('31') && digits.length >= 10) {
      // already valid format
    } else if (digits.startsWith('0') && digits.length >= 9) {
      digits = '31' + digits.substring(1);
    }
    return digits;
  };

  const getWhatsAppUrl = () => {
    const formattedPhone = phoneNumber.trim() ? formatPhoneForWhatsApp(phoneNumber) : '';
    return buildWhatsAppUrl(whatsappMessage, formattedPhone || undefined);
  };

  const handleOpenWhatsApp = () => {
    const url = getWhatsAppUrl();
    const win = window.open(url, '_blank');
    if (!win) {
      window.location.href = url;
    }
  };

  const handleCopyLink = async () => {
    if (await copyTextToClipboard(shareUrl)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" 
        onClick={onClose} 
      />

      {/* Modal Dialog */}
      <div className="bg-white dark:bg-[#15241b] w-full max-w-sm rounded-[28px] p-6 shadow-2xl relative z-10 animate-in zoom-in-95 duration-200 border border-gray-100 dark:border-white/10 max-h-[92vh] overflow-y-auto no-scrollbar">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#25D366]/20 text-[#25D366] flex items-center justify-center font-bold text-xl shadow-sm">
              <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.414-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.888 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">Deel via WhatsApp</h3>
              <p className="text-[11px] text-gray-500 dark:text-gray-400">Uitnodigen voor '{list.name}'</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <span className="material-symbols-outlined text-xl">close</span>
          </button>
        </div>

        {/* Notice when target user wasn't in the app yet */}
        {isUnknownUser && (
          <div className="mb-4 p-3.5 rounded-2xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/40 text-amber-900 dark:text-amber-200 text-xs">
            <div className="flex items-center gap-2 font-bold mb-1">
              <span className="material-symbols-outlined text-[18px] text-amber-600">person_search</span>
              <span>Nieuwe gebruiker</span>
            </div>
            <p className="leading-relaxed">
              {targetEmail ? (
                <><strong>{targetEmail}</strong> staat nog niet geregistreerd in de app.</>
              ) : (
                <>Deze persoon zit nog niet in superMarct.</>
              )} Stuur direct een WhatsApp-uitnodiging zodat diegene de app kan openen, installeren en direct kan meedoen!
            </p>
          </div>
        )}

        {/* Live of kopie */}
        <div className={`mb-4 p-3 rounded-2xl text-xs flex items-start gap-2 border ${isLive
          ? 'bg-primary/10 border-primary/20 text-gray-700 dark:text-gray-200'
          : 'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800/40 text-amber-900 dark:text-amber-200'}`}>
          <span className={`material-symbols-outlined text-[18px] ${isLive ? 'text-primary' : 'text-amber-600'}`}>{isLive ? 'sync' : 'content_copy'}</span>
          <p className="leading-relaxed">
            {isLive
              ? <><strong>Live samenwerken:</strong> wie deze link opent en inlogt, ziet wijzigingen direct en kan zelf toevoegen en afvinken.</>
              : <><strong>Alleen een kopie:</strong> log in met Google om de lijst live samen bij te houden.</>}
          </p>
        </div>

        {/* List Preview Card */}
        <div className="bg-surface-light dark:bg-black/20 rounded-2xl p-4 border border-gray-100 dark:border-white/5 mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-primary text-xl">shopping_cart</span>
              <span className="font-bold text-sm text-gray-900 dark:text-white">{list.name}</span>
            </div>
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary">
              {list.items?.length || 0} artikelen
            </span>
          </div>

          {list.items && list.items.length > 0 ? (
            <div className="space-y-1 pt-1">
              {list.items.slice(0, 3).map((item, i) => (
                <div key={item.id || i} className="text-xs text-gray-600 dark:text-gray-300 flex items-center justify-between">
                  <span className="truncate">• {item.name}</span>
                  {item.quantity && <span className="text-[10px] text-gray-400 font-medium ml-2">{item.quantity}</span>}
                </div>
              ))}
              {list.items.length > 3 && (
                <p className="text-[10px] text-gray-400 italic pt-1">...en nog {list.items.length - 3} andere artikelen</p>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic">Nog geen artikelen in deze lijst.</p>
          )}
        </div>

        {/* Phone Input (Optional) */}
        <div className="mb-4">
          <label className="text-[11px] font-bold text-gray-500 uppercase ml-1 mb-1 block">
            Telefoonnummer ontvanger (optioneel)
          </label>
          <div className="relative">
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="Bijv. 06 12345678 (of laat leeg voor chatkiezer)"
              className="w-full bg-gray-50 dark:bg-black/20 rounded-xl px-4 py-3 pl-10 text-sm font-medium border border-gray-200 dark:border-white/10 outline-none focus:border-[#25D366] transition-colors"
            />
            <span className="material-symbols-outlined text-gray-400 text-lg absolute left-3 top-3.5">
              call
            </span>
          </div>
          <p className="text-[10px] text-gray-400 mt-1 ml-1">
            Laat leeg om direct je WhatsApp-contactenlijst of chat te kiezen.
          </p>
        </div>

        {/* Message preview snippet */}
        <div className="mb-5 p-3 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/5 text-[11px] text-gray-600 dark:text-gray-300">
          <span className="text-[10px] uppercase font-bold text-gray-400 block mb-1">Berichtvoorbeeld:</span>
          <p className="italic line-clamp-3">
            "Hoi! 👋 {inviterName} wil graag de boodschappenlijst '{list.name}' met je delen via superMarct..."
          </p>
        </div>

        {/* Primary Buttons */}
        <div className="space-y-2.5">
          <button
            onClick={handleOpenWhatsApp}
            className="w-full py-3.5 rounded-2xl bg-[#25D366] hover:bg-[#22c35e] text-white font-bold text-sm shadow-lg shadow-green-500/25 active:scale-[0.98] transition-all flex items-center justify-center gap-2 cursor-pointer"
          >
            <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.414-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.888 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
            </svg>
            <span>Verstuur via WhatsApp</span>
          </button>

          <button
            onClick={handleCopyLink}
            className="w-full py-3 rounded-2xl bg-surface-light dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-200 font-bold text-xs hover:bg-gray-100 dark:hover:bg-white/10 active:scale-[0.98] transition-all flex items-center justify-center gap-2 cursor-pointer"
          >
            <span className="material-symbols-outlined text-[18px]">
              {copied ? 'check_circle' : 'content_copy'}
            </span>
            <span>{copied ? 'Uitnodigingslink gekopieerd!' : 'Kopieer uitnodigingslink'}</span>
          </button>

          <button
            onClick={onClose}
            className="w-full py-2.5 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 font-medium transition text-center"
          >
            Sluiten
          </button>
        </div>

      </div>
    </div>
  );
};
